import { createContext, ReactNode, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PhotoHelper } from '@/library';
import { PhotoService } from '@/service';

export type PhotoSectionState = {
  localPhotos: PhotoHelper.ILocalPhoto[];
  wedgeTypedPhotos: Partial<Record<PhotoHelper.WedgePhotoType, PhotoHelper.ILocalPhoto>>;
  editedPhotoCaptions: Record<number, string>;
  deletedStorageObjectIds: Set<number>;
  deletedTypedPhotoSlots: Set<PhotoHelper.WedgePhotoType>;
};

/* eslint-disable no-unused-vars */
interface PhotoCacheContextValue {
  getPhotoObjectUrl: (playerId: number, ownerRowGuid: string, fileName: string) => Promise<string>;
  getPhotoSectionState: (playerId: number, ownerRowGuid: string) => PhotoSectionState;
  setPhotoSectionLocalPhotos: (playerId: number, ownerRowGuid: string, updater: SetStateAction<PhotoHelper.ILocalPhoto[]>) => void;
  setPhotoSectionWedgeTypedPhotos: (playerId: number, ownerRowGuid: string, updater: SetStateAction<Partial<Record<PhotoHelper.WedgePhotoType, PhotoHelper.ILocalPhoto>>>) => void;
  setPhotoSectionEditedPhotoCaptions: (playerId: number, ownerRowGuid: string, updater: SetStateAction<Record<number, string>>) => void;
  setPhotoSectionDeletedStorageObjectIds: (playerId: number, ownerRowGuid: string, updater: SetStateAction<Set<number>>) => void;
  setPhotoSectionDeletedTypedPhotoSlots: (playerId: number, ownerRowGuid: string, updater: SetStateAction<Set<PhotoHelper.WedgePhotoType>>) => void;
}
/* eslint-enable no-unused-vars */

const PhotoCacheContext = createContext<PhotoCacheContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const usePhotoCache = () => {
  const context = useContext(PhotoCacheContext);
  if (!context) throw new Error('usePhotoCache must be used within PhotoCacheProvider');
  return context;
};

type PhotoSectionStates = Record<string, PhotoSectionState>;
/* eslint-disable no-unused-vars */
type PhotoSectionStateUpdater = (...args: [PhotoSectionState]) => PhotoSectionState;
/* eslint-enable no-unused-vars */

const createEmptyPhotoSectionState = (): PhotoSectionState => ({
  localPhotos: [],
  wedgeTypedPhotos: {},
  editedPhotoCaptions: {},
  deletedStorageObjectIds: new Set(),
  deletedTypedPhotoSlots: new Set(),
});

const getPhotoObjectUrlCacheKey = (playerId: number, ownerRowGuid: string, fileName: string) => {
  return `${playerId}:${ownerRowGuid}:${fileName}`;
};

const getPhotoSectionCacheKey = (playerId: number, ownerRowGuid: string) => {
  return `${playerId}:${ownerRowGuid}`;
};

const applyStateUpdater = <T,>(updater: SetStateAction<T>, currentValue: T) => {
  if (typeof updater === 'function') {
    return (updater as Function)(currentValue) as T;
  }

  return updater;
};

const revokePhotoSectionStateUrls = (sectionStates: PhotoSectionStates) => {
  Object.values(sectionStates).forEach((sectionState) => {
    sectionState.localPhotos.forEach((photo) => URL.revokeObjectURL(photo.src));
    Object.values(sectionState.wedgeTypedPhotos).forEach((photo) => {
      if (photo) URL.revokeObjectURL(photo.src);
    });
  });
};

interface PhotoCacheProviderProps {
  children: ReactNode;
}

export const PhotoCacheProvider = ({ children }: PhotoCacheProviderProps) => {
  const { playerId } = useParams<{ playerId: string }>();
  const [photoSectionStates, setPhotoSectionStates] = useState<PhotoSectionStates>({});
  const photoObjectUrlsRef = useRef(new Map<string, string>());
  const photoObjectUrlRequestsRef = useRef(new Map<string, Promise<string>>());
  const emptyPhotoSectionStateRef = useRef(createEmptyPhotoSectionState());

  const clearPhotoObjectUrlCache = useCallback(() => {
    photoObjectUrlsRef.current.forEach((photoObjectUrl) => URL.revokeObjectURL(photoObjectUrl));
    photoObjectUrlsRef.current.clear();
    photoObjectUrlRequestsRef.current.clear();
  }, []);

  const clearPhotoSectionStateCache = useCallback(() => {
    setPhotoSectionStates((currentStates) => {
      revokePhotoSectionStateUrls(currentStates);
      return {};
    });
  }, []);

  useEffect(() => {
    return () => {
      clearPhotoObjectUrlCache();
      clearPhotoSectionStateCache();
    };
  }, [clearPhotoObjectUrlCache, clearPhotoSectionStateCache, playerId]);

  const getPhotoObjectUrl = useCallback(async (photoPlayerId: number, ownerRowGuid: string, fileName: string) => {
    const cacheKey = getPhotoObjectUrlCacheKey(photoPlayerId, ownerRowGuid, fileName);
    const cachedObjectUrl = photoObjectUrlsRef.current.get(cacheKey);
    if (cachedObjectUrl) {
      return cachedObjectUrl;
    }

    const activeRequest = photoObjectUrlRequestsRef.current.get(cacheKey);
    if (activeRequest) {
      return activeRequest;
    }

    const photoObjectUrlRequest = PhotoService.fetchPhotoBlob(fileName)
      .then((photoBlob) => {
        const objectUrl = URL.createObjectURL(photoBlob);
        if (photoObjectUrlRequestsRef.current.get(cacheKey) !== photoObjectUrlRequest) {
          URL.revokeObjectURL(objectUrl);
          return objectUrl;
        }

        photoObjectUrlsRef.current.set(cacheKey, objectUrl);
        photoObjectUrlRequestsRef.current.delete(cacheKey);
        return objectUrl;
      })
      .catch((error) => {
        photoObjectUrlRequestsRef.current.delete(cacheKey);
        throw error;
      });

    photoObjectUrlRequestsRef.current.set(cacheKey, photoObjectUrlRequest);
    return photoObjectUrlRequest;
  }, []);

  const getPhotoSectionState = useCallback(
    (photoPlayerId: number, ownerRowGuid: string) => {
      const cacheKey = getPhotoSectionCacheKey(photoPlayerId, ownerRowGuid);
      return photoSectionStates[cacheKey] ?? emptyPhotoSectionStateRef.current;
    },
    [photoSectionStates],
  );

  const updatePhotoSectionState = useCallback((photoPlayerId: number, ownerRowGuid: string, updater: PhotoSectionStateUpdater) => {
    const cacheKey = getPhotoSectionCacheKey(photoPlayerId, ownerRowGuid);

    setPhotoSectionStates((currentStates) => {
      const currentState = currentStates[cacheKey] ?? createEmptyPhotoSectionState();
      return {
        ...currentStates,
        [cacheKey]: updater(currentState),
      };
    });
  }, []);

  const setPhotoSectionLocalPhotos = useCallback(
    (photoPlayerId: number, ownerRowGuid: string, updater: SetStateAction<PhotoHelper.ILocalPhoto[]>) => {
      updatePhotoSectionState(photoPlayerId, ownerRowGuid, (currentState) => ({
        ...currentState,
        localPhotos: applyStateUpdater(updater, currentState.localPhotos),
      }));
    },
    [updatePhotoSectionState],
  );

  const setPhotoSectionWedgeTypedPhotos = useCallback(
    (photoPlayerId: number, ownerRowGuid: string, updater: SetStateAction<Partial<Record<PhotoHelper.WedgePhotoType, PhotoHelper.ILocalPhoto>>>) => {
      updatePhotoSectionState(photoPlayerId, ownerRowGuid, (currentState) => ({
        ...currentState,
        wedgeTypedPhotos: applyStateUpdater(updater, currentState.wedgeTypedPhotos),
      }));
    },
    [updatePhotoSectionState],
  );

  const setPhotoSectionEditedPhotoCaptions = useCallback(
    (photoPlayerId: number, ownerRowGuid: string, updater: SetStateAction<Record<number, string>>) => {
      updatePhotoSectionState(photoPlayerId, ownerRowGuid, (currentState) => ({
        ...currentState,
        editedPhotoCaptions: applyStateUpdater(updater, currentState.editedPhotoCaptions),
      }));
    },
    [updatePhotoSectionState],
  );

  const setPhotoSectionDeletedStorageObjectIds = useCallback(
    (photoPlayerId: number, ownerRowGuid: string, updater: SetStateAction<Set<number>>) => {
      updatePhotoSectionState(photoPlayerId, ownerRowGuid, (currentState) => ({
        ...currentState,
        deletedStorageObjectIds: applyStateUpdater(updater, currentState.deletedStorageObjectIds),
      }));
    },
    [updatePhotoSectionState],
  );

  const setPhotoSectionDeletedTypedPhotoSlots = useCallback(
    (photoPlayerId: number, ownerRowGuid: string, updater: SetStateAction<Set<PhotoHelper.WedgePhotoType>>) => {
      updatePhotoSectionState(photoPlayerId, ownerRowGuid, (currentState) => ({
        ...currentState,
        deletedTypedPhotoSlots: applyStateUpdater(updater, currentState.deletedTypedPhotoSlots),
      }));
    },
    [updatePhotoSectionState],
  );

  return (
    <PhotoCacheContext.Provider
      value={{
        getPhotoObjectUrl,
        getPhotoSectionState,
        setPhotoSectionLocalPhotos,
        setPhotoSectionWedgeTypedPhotos,
        setPhotoSectionEditedPhotoCaptions,
        setPhotoSectionDeletedStorageObjectIds,
        setPhotoSectionDeletedTypedPhotoSlots,
      }}
    >
      {children}
    </PhotoCacheContext.Provider>
  );
};
